import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
} from "react";
import { Link, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import Button from "@mui/material/Button";

// Icons
import { RxDashboard } from "react-icons/rx";
import { FaAngleRight, FaAngleDown } from "react-icons/fa";
import { IoCreateOutline } from "react-icons/io5";
import { CiBank, CiSettings } from "react-icons/ci";
import { LuWarehouse } from "react-icons/lu";
import { TbReport } from "react-icons/tb";
import { MdSupportAgent } from "react-icons/md";
import { RiGroupLine } from "react-icons/ri";
import { CgProfile } from "react-icons/cg";
import { GiBrain } from "react-icons/gi";
import { LogOut } from "lucide-react";

import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { useMyContext } from "../../../context/MyContext";
import "../../../css/custom-scrollbar.css";
import { useAuth } from "../../../routes/Auth";

const Sidebar = () => {
  const { profile_flag, isDarkMode } = useMyContext();
  const [activeTab, setActiveTab] = useState(-1);
  const [pageTab, setPageTab] = useState(-1);
  const [activeSubTab, setActiveSubTab] = useState(-1);
  const { isToggleSidebar, setIsToggleSidebar, toggleB2BMode } =
    useMyContext();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const sidebarRef = useRef(null);
  const userRole = localStorage.getItem("role");

  // isToggleSidebar === true  -> full sidebar (icon + title)
  // isToggleSidebar === false -> collapsed icon-only rail

  // ============================================================
  // COLLAPSED-MODE TOOLTIP (rendered via portal to <body> so it is
  // never clipped by any parent's overflow-hidden / scroll area)
  // ============================================================

  const [tooltip, setTooltip] = useState({
    visible: false,
    text: "",
    top: 0,
    left: 0,
  });

  const showTooltip = useCallback((e, text, collapsedNow) => {
    if (!collapsedNow) return;
    const rect = e.currentTarget.getBoundingClientRect();
    setTooltip({
      visible: true,
      text,
      top: rect.top + rect.height / 2,
      left: rect.right + 10,
    });
  }, []);

  const hideTooltip = useCallback(() => {
    setTooltip((prev) => ({ ...prev, visible: false }));
  }, []);

  // ============================================================
  // MENU TOGGLE
  // ============================================================

  const toggleSubmenu = useCallback(
    (index, href) => {
      if (profile_flag === "0" && userRole !== "admin") {
        toast.error(
          "Please Contact to the Support Team, Your KYC is Pending"
        );
        return;
      }
      if (href) {
        navigate(href);
      } else {
        setActiveTab((prevActiveTab) =>
          prevActiveTab === index ? null : index
        );
      }
    },
    [profile_flag, userRole, navigate, setActiveTab]
  );

  // ============================================================
  // MENU ITEMS
  // ============================================================

  let menuItems = [
    {
      title: "Dashboard",
      icon: <RxDashboard />,
      href: "/b2b/dashboard",
      submenu: [],
      color: "#2563eb",
    },
     {
      title: "Shipments",
      href: "/b2b/shipments/list",
      icon: <IoCreateOutline />,
      submenu: [],
      color: "#9333ea",
    },
    {
      title: "Warehouse",
       href: "/b2b/warehouse/all" ,
      icon: <LuWarehouse />,
      submenu: [],
      color: "#059669",
    },
    {
      title: "Payments",
      icon: <CiBank />,
      submenu: [
        { title: "Wallet Balance", href: "/b2b/payments/wallet_recharge" },
      ],
      color: "#0891b2",
    },
    {
      title: "Customers",
      icon: <RiGroupLine />,
      submenu: [
        { title: "ViewAllCustomer", href: "/b2b/customers/viewCustomer" },
        { title: "KYC Pending", href: "/b2b/customers/kycPending" },
      ],
      color: "#16a34a",
    },
    {
      title: "Preship",
      icon: <GiBrain />,
      submenu: [
        { title: "Rate Calculator", href: "/b2b/preship/PriceEstimator" },
        { title: "Rate Card", href: "/b2b/preship/rateCard/" },
        { title: "Set Rate", href: "/b2b/preship/setRate" },
      ],
      color: "#4f46e5",
    },
    {
      title: "Reports",
      href: "/b2b/reports",
      icon: <TbReport />,
      submenu: [],
      color: "#0d9488",
    },
    {
      title: "Help",
      href: "/b2b/help",
      icon: <MdSupportAgent />,
      submenu: [],
      color: "#65a30d",
    },
    {
      title: "Setting",
      icon: <CiSettings />,
      submenu: [
        { title: "Add Employee", href: "b2b/addEmployee" },
        { title: "All Employee", href: "b2b/allEmployee" },
      ],
      color: "#525252",
    },
    {
      title: "Profile",
      icon: <CgProfile />,
      href: "/b2b/profile",
      submenu: [],
      color: "#0284c7",
    },
  ];

  // ============================================================
  // ROLE BASED MENU
  // ============================================================

  if (userRole !== "admin") {
    menuItems.forEach((item) => {
      if (item.submenu && item.title === "Disputes") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Weight Upload"
        );
      }
      if (item.submenu && item.title === "Disputes") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "All Weight Disputes"
        );
      }
    });
    menuItems = menuItems.filter((item) => item.title !== "Customers");
    menuItems.forEach((item) => {
      if (item.submenu && item.title === "Preship") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Set Rate"
        );
      }
    });
    menuItems = menuItems.filter((item) => item.title !== "Setting");
    menuItems.forEach((item) => {
      if (item.submenu && item.title === "Add Role") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Add Role"
        );
      }
    });
  } else {
    menuItems.forEach((item) => {
      if (item.submenu && item.title === "Preship") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Rate Card"
        );
      }
      if (item.submenu && item.title === "Preship") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Rate Calculator"
        );
      }
      if (item.submenu && item.title === "Disputes") {
        item.submenu = item.submenu.filter(
          (subItem) => subItem.title !== "Weight Disputes"
        );
      }
    });
  }

  // ============================================================
  // ACTIVE ROUTE
  // ============================================================

  useMemo(() => {
    let currRoute = window.location.pathname;
    menuItems.map((item, index) => {
      if (item.href && currRoute == item.href) {
        setActiveTab(index);
        setPageTab(index);
      }
      if (item.submenu) {
        item.submenu.map((subItem, subIndex) => {
          if (subItem.href && currRoute == subItem.href) {
            setActiveTab(index);
            setActiveSubTab(subIndex);
            setPageTab(index);
          }
        });
      }
    });
  }, []);

  // ============================================================
  // MOBILE OUTSIDE CLICK
  // ============================================================

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target) &&
        window.innerWidth <= 768
      ) {
        setIsToggleSidebar(false);
      }
    };
    if (isToggleSidebar) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isToggleSidebar, setIsToggleSidebar]);

  // ============================================================
  // LOGOUT
  // ============================================================

  const handleLogout = () => {
    toggleB2BMode(false);
    localStorage.removeItem("user");
    navigate("/login");
    logout();
    localStorage.removeItem("cus_id");
    localStorage.removeItem("token");
    toast.success("LogOut Sucessfully!");
    window.location.reload();
  };

  // ============================================================
  // MUI sx CAN'T READ TAILWIND `dark:` CLASSES,
  // SO WE BRANCH MANUALLY HERE USING isDarkMode
  // ============================================================

  const activeBg = isDarkMode
    ? "linear-gradient(90deg, rgba(255,255,255,0.10), rgba(255,255,255,0.06), rgba(255,255,255,0.03))"
    : "linear-gradient(90deg, rgba(255,255,255,0.95), rgba(243,244,246,0.8), rgba(229,231,235,0.65))";

  const activeBorder = isDarkMode
    ? "1px solid rgba(255,255,255,0.08)"
    : "1px solid rgba(0,0,0,0.04)";

  const activeShadow = isDarkMode
    ? "0 1px 2px rgba(0,0,0,0.35), 0 4px 10px rgba(0,0,0,0.3), 0 10px 24px rgba(0,0,0,0.25)"
    : "0 1px 2px rgba(16,24,40,0.04), 0 4px 10px rgba(16,24,40,0.06), 0 10px 24px rgba(16,24,40,0.05)";

  const activeTextColor = isDarkMode ? "#f1f5f9" : "#1f2937";
  const defaultTextColor = isDarkMode
    ? "rgba(203,213,225,0.75)"
    : "rgba(75,85,99,0.75)";

  const isCollapsed = !isToggleSidebar;

  // ============================================================
  // SIDEBAR
  // ============================================================

  return (
    <>
      <ToastContainer position="top-right" autoClose={1000} />
      <aside
        ref={sidebarRef}
        className={`
          relative
          ${isCollapsed ? "w-20" : "w-64"}
          h-full
          flex
          flex-col
          overflow-hidden
          text-gray-700
          dark:text-gray-200
          sidebar-glass
          transition-all
          duration-300
          ease-in-out
        `}
      >
        {/* =====================================================
            BACKGROUND
        ===================================================== */}

        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute inset-0 bg-white dark:bg-[#12162a]" />

          <div
            className="
              absolute
              top-0
              left-0
              right-0
              h-24
              bg-gradient-to-b
              from-gray-50/80
              via-white/40
              to-transparent
              dark:from-white/[0.03]
              dark:via-white/[0.0]
              dark:to-transparent
            "
          />

          <div
            className="
              absolute
              top-0
              right-0
              bottom-0
              w-px
              bg-gray-200/70
              dark:bg-white/[0.06]
            "
          />

          <div
            className="
              absolute
              bottom-0
              left-0
              right-0
              h-16
              bg-gradient-to-t
              from-gray-900/[0.02]
              to-transparent
              dark:from-black/25
              dark:to-transparent
            "
          />
        </div>

        {/* =====================================================
            CONTENT
        ===================================================== */}

        <div className="relative z-10 flex flex-col h-full">
          {/* =================================================
              MENU CONTAINER
          ================================================= */}

          <div className="flex-1 overflow-hidden">
            <div
              className="h-full overflow-y-auto overflow-x-hidden px-3 pt-4 [scrollbar-width:none] [-ms-overflow-style:none] [&::-webkit-scrollbar]:hidden [&::-webkit-scrollbar]:w-0"
              style={{ overflowX: "hidden" }}
            >
              <ul className="">
                {menuItems.map((item, index) => {
                  const isActive = activeTab === index;

                  return (
                    <li
                      key={index}
                      className="group relative"
                      onMouseEnter={(e) => showTooltip(e, item.title, isCollapsed)}
                      onMouseLeave={hideTooltip}
                    >
                      {/* =================================================
                          MAIN MENU BUTTON
                      ================================================= */}

                      <Button
                        onClick={() => toggleSubmenu(index, item.href)}
                        sx={{
                          minWidth: 0,
                          fontFamily: "var(--font-custom-open-sans)",
                          textTransform: "none",
                          width: "100%",
                          textAlign: "left",
                          fontWeight: 500,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: isCollapsed
                            ? "center"
                            : "flex-start",
                          gap: isCollapsed ? 0 : "10px",
                          padding: isCollapsed ? "9px 0" : "9px 12px",
                          borderRadius: "16px",
                          position: "relative",
                          overflow: "hidden",
                          transition:
                            "all 0.3s cubic-bezier(0.16, 1, 0.3, 1)",

                          color: isActive ? activeTextColor : defaultTextColor,
                          background: isActive ? activeBg : "transparent",
                          border: isActive
                            ? activeBorder
                            : "1px solid transparent",
                          boxShadow: isActive ? activeShadow : "none",

                          "&:hover": {
                            background: activeBg,
                            border: activeBorder,
                            color: activeTextColor,
                            boxShadow: activeShadow,
                          },
                        }}
                      >
                        {/* =================================================
                            ICON — dark rounded badge
                        ================================================= */}
                        <span
                          className={`
                            relative
                            z-10
                            flex
                            items-center
                            justify-center
                            w-7
                            h-7
                            rounded-full
                            text-[14px]
                            shrink-0
                            transition-all
                            duration-300
                            ${
                              isActive
                                ? "shadow-lg dark:bg-slate-100 dark:text-gray-900 group-hover:text-white dark:group-hover:bg-slate-100"
                                : "bg-transparent text-gray-400 dark:text-gray-500 group-hover:bg-gray-900 group-hover:text-white dark:group-hover:bg-slate-100 dark:group-hover:text-gray-900"
                            }
                          `}
                          style={isActive ? { color: item.color } : {}}
                          onMouseEnter={(e) => {
                            if (!isActive)
                              e.currentTarget.style.backgroundColor =
                                item.color;
                          }}
                          onMouseLeave={(e) => {
                            if (!isActive)
                              e.currentTarget.style.backgroundColor = "";
                          }}
                        >
                          {item.icon}
                        </span>

                        {/* =================================================
                            TITLE (hidden when collapsed)
                        ================================================= */}

                        {!isCollapsed && (
                          <span
                            className={`
                              relative
                              z-10
                              flex-1
                              text-left
                              text-[12px]
                              font-medium
                              transition-colors
                              duration-300
                              ${
                                isActive
                                  ? "text-gray-800 dark:text-slate-100"
                                  : "text-gray-600 dark:text-slate-400 group-hover:text-gray-800 dark:group-hover:text-slate-100"
                              }
                            `}
                          >
                            {item.title}
                          </span>
                        )}

                        {/* =================================================
                            ARROW (hidden when collapsed)
                        ================================================= */}

                        {!isCollapsed && item.submenu.length > 0 && (
                          <span
                            className={`
                              relative
                              z-10
                              text-[9px]
                              mr-1
                              transition-all
                              duration-300
                              ${
                                isActive
                                  ? "text-gray-500 dark:text-slate-400 rotate-180"
                                  : "text-gray-400 dark:text-slate-500 group-hover:text-gray-600 dark:group-hover:text-slate-300"
                              }
                            `}
                          >
                            {isActive ? <FaAngleDown /> : <FaAngleRight />}
                          </span>
                        )}
                      </Button>

                      {/* =================================================
                          SUBMENU (only in expanded mode)
                      ================================================= */}

                      {!isCollapsed && item.submenu.length > 0 && isActive && (
                        <div className="relative ml-5 mt-1 mb-1">
                          <ul className="relative z-10 space-y-0.5 py-1 px-0.5">
                            {item.submenu.map((subItem, subIndex) => {
                              const isSubActive =
                                activeSubTab === subIndex;

                              return (
                                <li key={subIndex}>
                                  <Link
                                    to={subItem.href}
                                    onClick={() =>
                                      setActiveSubTab(subIndex)
                                    }
                                    className={`
                                      flex
                                      items-center
                                      px-3
                                      py-2
                                      rounded-full
                                      text-[10px]
                                      font-medium
                                      transition-all
                                      duration-300
                                      border
                                      ${
                                        isSubActive
                                          ? "text-gray-800 bg-white border-black/[0.04] shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_10px_rgba(16,24,40,0.06)] dark:text-slate-100 dark:bg-white/[0.08] dark:border-white/[0.08]"
                                          : "text-gray-500 border-transparent hover:text-gray-800 hover:bg-white hover:border-black/[0.04] hover:shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_10px_rgba(16,24,40,0.06)] dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-white/[0.06] dark:hover:border-white/[0.08]"
                                      }
                                    `}
                                  >
                                    {isSubActive && (
                                      <span className="w-1 h-1 rounded-full bg-gray-900 dark:bg-slate-100 mr-2" />
                                    )}
                                    <span>{subItem.title}</span>
                                  </Link>
                                </li>
                              );
                            })}
                          </ul>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          </div>

          {/* =================================================
              LOGOUT
          ================================================= */}

          <div className="px-3 pb-3 pt-2">
            <div className="h-px bg-gray-200/70 dark:bg-white/[0.08] mb-3" />

            <div
              className="relative group"
              onMouseEnter={(e) => showTooltip(e, "Logout", isCollapsed)}
              onMouseLeave={hideTooltip}
            >
              <button
                onClick={handleLogout}
                className={`
                  group
                  relative
                  w-full
                  flex
                  items-center
                  justify-center
                  ${isCollapsed ? "gap-0 px-0" : "gap-2.5 px-3"}
                  py-2
                  rounded-2xl
                  text-left
                  text-[11px]
                  font-medium
                  text-white
                  bg-red-600
                  border
                  border-transparent
                  transition-all
                  duration-300
                  hover:bg-gray-300
                  hover:border-black/[0.04]
                  hover:shadow-[0_1px_2px_rgba(16,24,40,0.04),0_4px_10px_rgba(16,24,40,0.06),0_10px_24px_rgba(16,24,40,0.05)]
                  hover:text-gray-600
                  active:scale-[0.98]
                `}
              >
                <div
                  className="
                    flex
                    items-center
                    justify-center
                    w-7
                    h-7
                    rounded-full
                    border
                    border-gray-100/70
                    dark:border-white/20
                    text-white
                    transition-all
                    duration-300
                    group-hover:bg-gray-900
                    group-hover:text-white
                    group-hover:border-transparent
                  "
                >
                  <LogOut color="white" size={13} />
                </div>

                {!isCollapsed && <span className="text-[14px]">Logout</span>}
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* =====================================================
          PORTAL TOOLTIP — rendered directly on <body>, so it is
          never clipped/scrolled by the sidebar's overflow areas
      ===================================================== */}
      {tooltip.visible &&
        createPortal(
          <div
            style={{
              position: "fixed",
              top: tooltip.top,
              left: tooltip.left,
              transform: "translateY(-50%)",
              zIndex: 99999,
            }}
            className={`
              pointer-events-none
              whitespace-nowrap
              rounded-md
              bg-gray-800
              dark:bg-indigo-950
              dark:border
              dark:border-indigo-500/40
              px-2.5
              py-1
              text-[11px]
              text-white
              dark:text-indigo-100
              shadow-lg
            `}
          >
            {tooltip.text}
          </div>,
          document.body
        )}
    </>
  );
};

export default Sidebar;